/**
 * Idioma y resolución de labels para Dual CTA (variantes admin + visibility).
 *
 * Modelo admin: cada variante guarda columnas `*_es` y `*_en` solamente.
 *   · ES → bucket español
 *   · EN, PT, DE, FR (y cualquier otro) → bucket inglés/internacional
 *
 * Los defaults de UI (loading, botones sin override) viven en el front
 * (`dualCtaLandingStrings`) con traducción para los idiomas soportados del sitio.
 */

export type DualCtaLang = "es" | "en" | "pt" | "de" | "fr" | "it";

const DUAL_CTA_LANGS: DualCtaLang[] = ["es", "en", "pt", "de", "fr", "it"];

export function normalizeDualCtaLang(raw: string | undefined | null): DualCtaLang {
  const base = (raw || "es").toLowerCase().split("-")[0];
  if ((DUAL_CTA_LANGS as string[]).includes(base)) return base as DualCtaLang;
  return "en";
}

/** Variantes / visibility / dual-cta-labels API usan solo columnas ES + EN. */
export function dualCtaLangUsesSpanishBucket(lang: DualCtaLang): boolean {
  return lang === "es";
}

/** Mapea idioma del visitante al bucket que entiende la API/DB (es | en). */
export function dualCtaLangToAdminBucket(lang: string | undefined | null): "es" | "en" {
  return dualCtaLangUsesSpanishBucket(normalizeDualCtaLang(lang)) ? "es" : "en";
}

type LangFieldRow = Record<string, unknown>;

export function pickDualCtaLangField(
  row: LangFieldRow | null | undefined,
  base: "vip_label" | "vip_subtitle" | "free_label",
  language: string | undefined | null,
): string | null {
  if (!row) return null;
  const lang = normalizeDualCtaLang(language);
  const useEs = dualCtaLangUsesSpanishBucket(lang);
  const primary = row[useEs ? `${base}_es` : `${base}_en`];
  if (typeof primary === "string" && primary.trim()) return primary.trim();
  const alt = row[useEs ? `${base}_en` : `${base}_es`];
  return typeof alt === "string" && alt.trim() ? alt.trim() : null;
}

export interface DualCtaVisibilityLabels {
  vipLabelEs?: string | null;
  vipLabelEn?: string | null;
  freeLabelEs?: string | null;
  freeLabelEn?: string | null;
}

export function pickDualCtaVisibilityLabel(
  labels: DualCtaVisibilityLabels,
  field: "vip" | "free",
  language: string | undefined | null,
): string | null {
  const lang = normalizeDualCtaLang(language);
  const useEs = dualCtaLangUsesSpanishBucket(lang);
  const esKey = field === "vip" ? "vipLabelEs" : "freeLabelEs";
  const enKey = field === "vip" ? "vipLabelEn" : "freeLabelEn";
  const primary = useEs ? labels[esKey] : labels[enKey];
  if (typeof primary === "string" && primary.trim()) return primary.trim();
  const alt = useEs ? labels[enKey] : labels[esKey];
  return typeof alt === "string" && alt.trim() ? alt.trim() : null;
}

export interface DualCtaVariantLabelSource {
  vipLabel?: string | null;
  vipSubtitle?: string | null;
  freeLabel?: string | null;
  vipLabelEs?: string | null;
  vipLabelEn?: string | null;
  vipSubtitleEs?: string | null;
  vipSubtitleEn?: string | null;
  freeLabelEs?: string | null;
  freeLabelEn?: string | null;
}

export function pickDualCtaVariantVipLabel(
  variant: DualCtaVariantLabelSource | null | undefined,
  language: string | undefined | null,
): string | null {
  if (!variant) return null;
  const fromRaw = pickDualCtaLangField(variant as LangFieldRow, "vip_label", language);
  if (fromRaw) return fromRaw;
  const legacy = variant.vipLabel?.trim();
  return legacy || null;
}

export function pickDualCtaVariantFreeLabel(
  variant: DualCtaVariantLabelSource | null | undefined,
  language: string | undefined | null,
): string | null {
  if (!variant) return null;
  const fromRaw = pickDualCtaLangField(variant as LangFieldRow, "free_label", language);
  if (fromRaw) return fromRaw;
  const legacy = variant.freeLabel?.trim();
  return legacy || null;
}